/**
 * Feature flags for the fuel app.
 *
 * Controlled via env vars so we can build + test behavior in staging/dev
 * without shipping it live. Flipping a flag in production is a single
 * `NEXT_PUBLIC_*` env var change + redeploy — no code changes needed.
 *
 * All flags are `NEXT_PUBLIC_*` so they inline into the client bundle at
 * build time. This means flag state is a build-time decision, not a
 * runtime one. If you need to flip a flag, redeploy — don't hot-swap.
 */

/**
 * Master switch for SEO / LLM discoverability features.
 *
 * When ON (env var set to "true"):
 *   - Full Schema.org markup emitted
 *   - llms.txt + llms-full.txt served with rich product description
 *   - robots.txt allows AI crawlers (GPTBot, ClaudeBot, PerplexityBot)
 *   - Sitemap advertises all public routes
 *   - Meta tags optimized for search intent keywords
 *
 * When OFF (default):
 *   - robots.txt is restrictive (no AI crawlers, no deep indexing)
 *   - Schema.org markup is minimal
 *   - llms.txt returns 404
 *   - SEO tags default to safe minimums
 *
 * Set to `'true'` in Render env vars when you're ready to launch publicly.
 * Takes effect on next deploy.
 */
export function isDiscoverable(): boolean {
	return process.env.NEXT_PUBLIC_DISCOVERABILITY_MODE === 'true'
}

/**
 * The canonical production host for this app — used for:
 *   - <link rel="canonical"> on the homepage
 *   - OpenGraph `og:url` tag
 *   - Sitemap entries
 *   - llms.txt and robots.txt URL references
 *   - 301 redirect source (future: redirect onrender.com → canonical)
 *
 * Default: `fuel-estimator.onrender.com` (the Render-provisioned URL). Set
 * `NEXT_PUBLIC_CANONICAL_HOST` if a custom domain is attached. Keep this
 * as a single source of truth — every place that emits a URL reads it
 * from here.
 */
export function getCanonicalHost(): string {
	return process.env.NEXT_PUBLIC_CANONICAL_HOST ?? 'fuel-estimator.onrender.com'
}

/**
 * The Plausible Analytics **site identity** (the `data-domain` on the tracking
 * script). This is DELIBERATELY decoupled from `getCanonicalHost()`.
 *
 * Why they must differ:
 *   - `getCanonicalHost()` tracks the *live serving host* for SEO (canonical
 *     tags, OG URLs, sitemap), which can move between hosts.
 *   - Plausible's site id must EXACTLY match a site registered in the
 *     Plausible dashboard. Plausible **silently drops every event whose site
 *     is not registered**, so this stays a stable identity even if the served
 *     host changes. To keep history across a host move, rename the site in
 *     Plausible (Site Settings → General → Change domain) rather than
 *     registering a new one.
 *
 * Used by the stats API (`plausible-stats.ts`) as the `site_id`. Override with `NEXT_PUBLIC_PLAUSIBLE_DOMAIN` ONLY if you rename/re-register
 * the Plausible site. It must equal the site slug shown in the Plausible UI.
 */
export function getPlausibleDomain(): string {
	return process.env.NEXT_PUBLIC_PLAUSIBLE_DOMAIN ?? 'fuel-estimator.onrender.com'
}

/**
 * The canonical full URL (scheme + host) for a given path.
 * Used for canonical tags, OG URLs, sitemap entries.
 *
 * Always HTTPS — all our deployment targets (Render, custom domain) use
 * TLS. If you ever run HTTP-only somewhere, override via another flag.
 */
export function canonicalUrl(path = '/'): string {
	const host = getCanonicalHost()
	const normalized = path.startsWith('/') ? path : `/${path}`
	return `https://${host}${normalized}`
}

/**
 * Hosts that should NEVER appear in search results, regardless of the
 * discoverability flag — e.g. staging/preview subdomains.
 *
 * Empty today: the onrender host IS the canonical host, so it is the URL we
 * WANT indexed. If a custom domain becomes canonical, add the onrender host
 * here so it doesn't split SEO with the real domain.
 *
 * Add any future non-canonical hosts here (staging, preview, etc.).
 */
const ALWAYS_BLOCKED_HOSTS = new Set<string>([])

/**
 * Returns true if the request's Host should be blocked from all
 * discoverability (robots Disallow, llms.txt 404, etc.) regardless
 * of the discoverability flag state.
 *
 * Pass the `Host` header value from the incoming request. If the host
 * is the production canonical or any unknown value, returns false.
 */
export function isAlwaysBlockedHost(host: string | null | undefined): boolean {
	if (!host) return false
	// Strip port if present (e.g., "localhost:3000")
	const bare = host.split(':')[0]?.toLowerCase() ?? ''
	return ALWAYS_BLOCKED_HOSTS.has(bare)
}

/**
 * Smart Fuel Plan — SHELVED 2026-04-19 pending UX rethink.
 *
 * Unlike the env-driven flags above, this is a **hardcoded code-level
 * shelving decision**. The Smart Fuel Plan feature works algorithmically
 * but its UI surfacing creates confusing competing "totals" with the
 * State Breakdown, and the on-the-fly comparison overstates savings on
 * single-PADD routes. Detailed rationale + open UX questions live in
 * fuel-estimator.tsx where the flag is consumed.
 *
 * To re-enable end-to-end (across vehicle picker, tank input, plan panel,
 * map markers): flip this constant. All wiring is preserved.
 *
 * Centralized here (not inline in fuel-estimator.tsx) so multiple UI
 * surfaces can gate consistently — vehicle picker hides tank capacity
 * column, fuel estimator hides the input + panel + map markers.
 */
export const FUEL_PLAN_ENABLED: boolean = false
